import type { Currency } from '../../types/currency';
import type { ApiLocale, TranslationLocale } from '../../types/locale';

type MarketConfig = {
  locale?: TranslationLocale;
  currency?: Currency;
};

function getConfig(marketConfig?: MarketConfig) {
  const appEnv = process.env.APP_ENV ?? 'production';
  const isNonProduction = appEnv !== 'production';

  return {
    version: process.env.VERSION || '0.0.1',
    isProduction: appEnv === 'production',
    isNonProduction,
    isStaging: appEnv === 'staging',
    isTest: appEnv === 'test',
    isDevelopment: appEnv === 'development',
    isMockingEnabled: ['true', true].includes(process.env.API_MOCKING || ''),
    isDebugEnabled: process.env.DEBUG === 'true',
    isAutologinCookieEnabled: appEnv === 'development' && process.env.AUTOLOGIN_COOKIE_ENABLED === 'true',
    appEnv,
    baseUrl: process.env.BASE_URL || 'https://lottoland.co.uk',
    apiUrl: process.env.API_URL || 'http://localhost:3000',
    apiKey: process.env.API_KEY || 'AABB138',
    gameMode: (process.env.GAME_MODE || 'REAL') as 'DEMO' | 'REAL',
    branch: process.env.DEPLOY_BRANCH || '',
    commitSha: process.env.DEPLOY_VERSION || '',
    sportsbook: {
      env: (process.env.SPORTSBOOK_ENV || 'STAGE') as 'PROD' | 'STAGE',
      integration: process.env.SPORTSBOOK_INTEGRATION || 'lottoland23test',
    },
    addressLookup: {
      apiKey: process.env.LOQATE_KEY || '',
      baseUrl: process.env.LOQATE_URL || 'https://api.addressy.com',
    },
    feedback: {
      apiKey: process.env.FEEDBACK_API_KEY || '',
    },
    legacySiteUrl: process.env.LEGACY_SITE_URL || process.env.REGISTER_REDIRECT_URL || 'https://lottoland.co.uk',
    tracking: {
      gtm: {
        enabled: process.env.GTM_ENABLED === 'true',
        id: process.env.GTM_ID || '',
        baseUrl: process.env.GTM_BASE_URL || 'https://st.lottoland.co.uk',
      },
      fullStory: {
        enabled: process.env.FULL_STORY_ENABLED === 'true',
        orgId: process.env.FULL_STORY_ORG || '',
      },
      oneTrust: {
        domainScript: process.env.ONETRUST_DOMAIN || '01995ca2-7a2f-7210-8f1c-49809035ca4d',
      },
      domain: process.env.COOKIE_DOMAIN,
      abTasty: {
        enabled: process.env.AB_TASTY_ENABLED === 'true',
        scriptUrl: process.env.AB_TASTY_SCRIPT_URL || '',
      },
      optimove: {
        enabled: process.env.OPTIMOVE_ENABLED === 'true',
        tenantId: process.env.OPTIMOVE_TENANT_ID || '',
        tenantToken: process.env.OPTIMOVE_TENANT_TOKEN || '',
      },
    },
    observability: {
      datadog: {
        enabled: process.env.DATADOG_RUM_ENABLED === 'true',
        rumApplicationId: process.env.DATADOG_RUM_APPLICATION_ID || '',
        clientToken: process.env.DATADOG_RUM_CLIENT_TOKEN || '',
        site: process.env.DATADOG_RUM_SITE || 'datadoghq.eu',
        service: process.env.DATADOG_RUM_SERVICE || 'lottoland-fe',
        sessionSampleRate: Number(process.env.DATADOG_RUM_SESSION_SAMPLE_RATE || 100),
        sessionReplaySampleRate: Number(process.env.DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE || 0),
      },
    },
    locale: marketConfig?.locale ?? 'en-GB',
    apiLocale: (process.env.API_LOCALE as ApiLocale | undefined) ?? 'en_GB',
    currency: marketConfig?.currency ?? 'GBP',
    baseImgUrl: process.env.IMG_BASE_URL || 'https://beta.lottoland.co.uk',
    // Runtime KenoGO iframe game bundle URL (ADR 0035 — not NEXT_PUBLIC_*)
    kenogoGameBaseUrl: process.env.KENOGO_GAME_BASE_URL || 'https://kenogo.lttlapp.com/kenoGo/',
    // Enable mock player feature in non-production environments only (security check)
    // Actual detection (cookie/header/env var) is done in shouldMockLoggedInPlayer()
    mockLoggedInPlayer: isNonProduction,
    features: {
      sportsbookLegacyRedirectionEnabled: process.env.SPORTSBOOK_LEGACY_REDIRECTION_ENABLED === 'true',
      scratchcardsLegacyRedirectionEnabled: process.env.SCRATCHCARDS_LEGACY_REDIRECTION_ENABLED === 'true',
      magazineLegacyRedirectionEnabled: process.env.MAGAZINE_LEGACY_REDIRECTION_ENABLED === 'true',
    },
  };
}

export type Config = ReturnType<typeof getConfig>;

export { getConfig };
